// Klientská část funkcí převzatých z GEP/Design: relace administrátora s 2FA, import obrázků,
// online počítadla a návrh podle webu. Tiskový výstup se tu nijak neřeší.
import { auth } from './firebase';
import { getAppCheckHeader } from './firebase';

const SESSION_KEY = 'samolepky-admin-session';
const DEVICE_KEY = 'samolepky-admin-device';
// Prohlížeč, ve kterém se někdy přihlásil administrátor – nepočítá se do návštěv zákazníků (jako v GEP)
export const ADMIN_BROWSER_KEY = 'samolepky-admin-browser';
export const markAdminBrowser = () => { try { localStorage.setItem(ADMIN_BROWSER_KEY, '1'); } catch { /* */ } };
// ID relace pro statistiky AI (počet různých zákazníků); ukládá se jen jeho otisk
export const clientSidHeader = (): Record<string, string> => { try { const s = sessionStorage.getItem('samolepky-sid'); return s ? { 'X-Client-Sid': s } : {}; } catch { return {}; } };

export const adminSession = {
    get: () => { try { return sessionStorage.getItem(SESSION_KEY) || ''; } catch { return ''; } },
    getDevice: () => { try { return localStorage.getItem(DEVICE_KEY) || ''; } catch { return ''; } },
    set: (session?: string | null, device?: string | null) => {
        try {
            if (session) sessionStorage.setItem(SESSION_KEY, session);
            if (device) localStorage.setItem(DEVICE_KEY, device);
        } catch { /* soukromý režim */ }
    },
    clear: (alsoDevice = false) => {
        try { sessionStorage.removeItem(SESSION_KEY); if (alsoDevice) localStorage.removeItem(DEVICE_KEY); } catch { /* */ }
    },
};

export function adminSessionHeaders(): Record<string, string> {
    const h: Record<string, string> = {};
    const s = adminSession.get(); if (s) h['X-Admin-Session'] = s;
    const d = adminSession.getDevice(); if (d) h['X-Admin-Device'] = d;
    return h;
}

async function idTokenHeader(): Promise<Record<string, string>> {
    const u = auth?.currentUser;
    if (!u) return {};
    try { return { Authorization: `Bearer ${await u.getIdToken()}` }; } catch { return {}; }
}

export async function adminFetch<T = any>(url: string, init: { method?: string; body?: any } = {}): Promise<T> {
    const res = await fetch(url, {
        method: init.method || (init.body ? 'POST' : 'GET'),
        headers: { 'Content-Type': 'application/json', ...(await idTokenHeader()), ...adminSessionHeaders() },
        body: init.body ? JSON.stringify(init.body) : undefined,
        cache: 'no-store',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `Chyba ${res.status}`), { status: res.status, twofa: !!data.twofa });
    return data as T;
}

// Nahrání souboru do administrace (multipart, pole "file") – např. ICC profil pro tiskové PDF.
export async function adminUpload<T = any>(url: string, file: File): Promise<T> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(url, { method: 'POST', headers: { ...(await idTokenHeader()), ...adminSessionHeaders() }, body: fd, cache: 'no-store' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `Chyba ${res.status}`), { status: res.status, twofa: !!data.twofa });
    return data as T;
}

export interface TwofaInfo { enabled: boolean; verified: boolean; required: boolean; lockedUntil: number | null; recoveryLeft: number }
export interface WhoAmI { role: string; admin: boolean; email?: string; refreshToken?: boolean; twofa?: TwofaInfo }

export const whoami = () => adminFetch<WhoAmI>('/api/admin/whoami');

export async function reportLogin(ok: boolean, method: 'password' | 'google', email?: string, code?: string) {
    try {
        await fetch('/api/admin/login-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(ok ? await idTokenHeader() : {}) },
            body: JSON.stringify({ ok, method, email, code }),
        });
    } catch { /* záznam není kritický */ }
}

// ---------- import obrázků ----------
const NATIVE = /^image\/(png|jpe?g|webp|gif|svg\+xml)$/i;
export const IMAGE_ACCEPT = 'image/*,.heic,.heif,.psd,.pdf,.ai,.eps,.tif,.tiff,.bmp,.jxl,.jp2,.svg';

function readAsDataUrl(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error('Soubor se nepodařilo přečíst.'));
        r.readAsDataURL(file);
    });
}

// Vrací data URL, kterou umí zobrazit prohlížeč i tiskový export. Běžné formáty se čtou přímo
// (stejně jako dřív), ostatní (HEIC, PSD, PDF/AI/EPS, TIFF, BMP…) převede server na PNG.
export async function fileToDataUrl(file: File): Promise<{ dataUrl: string; isSvg: boolean; converted: boolean }> {
    if (NATIVE.test(file.type)) return { dataUrl: await readAsDataUrl(file), isSvg: file.type.includes('svg'), converted: false };
    const fd = new FormData();
    fd.append('file', file, file.name || 'obrazek');
    const res = await fetch('/api/convert-image', { method: 'POST', headers: { ...(await getAppCheckHeader()) }, body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Soubor se nepodařilo převést.');
    return { dataUrl: data.dataUrl, isSvg: false, converted: true };
}

export async function imageFromUrl(url: string): Promise<{ dataUrl: string; name: string }> {
    const res = await fetch('/api/fetch-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAppCheckHeader()) },
        body: JSON.stringify({ url }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Obrázek se nepodařilo načíst.');
    return { dataUrl: data.dataUrl, name: (data.name || 'obrazek').replace(/\.[a-z0-9]+$/i, '') };
}

export interface BrandResult { site: string; title: string; colors: string[]; logos: { source: string; width: number; height: number; dataUrl: string }[] }
export async function brandFromWeb(url: string): Promise<BrandResult> {
    const res = await fetch('/api/brand-from-web', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAppCheckHeader()) },
        body: JSON.stringify({ url }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Web se nepodařilo načíst.');
    return data;
}

// Čitelná barva písma na zadaném pozadí
export function contrastText(hex: string): string {
    const m = hex.replace('#', '');
    const [r, g, b] = [0, 2, 4].map(i => parseInt(m.slice(i, i + 2), 16) / 255).map(c => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#111111' : '#ffffff';
}

// ---------- online počítadla ----------
let liveStarted = false;
export function startLivePing() {
    if (liveStarted || typeof window === 'undefined') return;
    liveStarted = true;
    let sid = '';
    try { sid = sessionStorage.getItem('samolepky-sid') || ''; } catch { /* */ }
    let first = false;
    if (!sid) {
        sid = (crypto as any).randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
        try { sessionStorage.setItem('samolepky-sid', sid); } catch { /* */ }
        first = true;
    }
    let interacted = false, reportedInteraction = false;
    const markInteraction = () => { interacted = true; };
    ['pointerdown', 'keydown'].forEach(ev => window.addEventListener(ev, markInteraction, { once: true, passive: true }));
    const params = new URLSearchParams(location.search);
    const utm = params.get('utm_source') || '';
    const ping = () => {
        if (document.visibilityState === 'hidden') return;
        let admin = false; try { admin = localStorage.getItem(ADMIN_BROWSER_KEY) === '1'; } catch { /* */ }
        const body = { sid, first, interacted, firstInteraction: interacted && !reportedInteraction, ref: first ? document.referrer : '', utm: first ? utm : '', page: 'editor', admin };
        if (interacted) reportedInteraction = true;
        first = false;
        fetch('/api/live/ping', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(() => {});
    };
    ping();
    setInterval(ping, 30000);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') ping(); });
}

// ---------- odhad doby AI generování ----------
const AI_TIME_KEY = 'samolepky-ai-durations';
export function expectedAiSeconds(): number {
    try {
        const arr: number[] = JSON.parse(localStorage.getItem(AI_TIME_KEY) || '[]');
        if (arr.length) return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length / 1000);
    } catch { /* */ }
    return 20;
}
export function recordAiDuration(ms: number) {
    try {
        const arr: number[] = JSON.parse(localStorage.getItem(AI_TIME_KEY) || '[]');
        arr.push(ms);
        localStorage.setItem(AI_TIME_KEY, JSON.stringify(arr.slice(-5)));
    } catch { /* */ }
}
